import React from "react"
import { createGlobalStyle, ThemeProvider } from "styled-components"
import { MDXProvider } from "@mdx-js/react"
import { preToCodeBlock } from "mdx-utils"
import Theme from "./src/themes/theme"
import { Table, Code } from "./src/components"
import "./language-tabs.css"

const GlobalStyles = createGlobalStyle`
    :root {
        --bg: #f4efe6;
        --elevated: #fbf7f0;
        --fg: #1c1915;
        --muted: #6b6459;
        --subtle: #8a8276;
        --accent: #3d5a4c;
        --accent-fg: #f4efe6;
        --border: rgba(28, 25, 21, 0.12);
        --font-serif: "Newsreader", "Iowan Old Style", Palatino, Georgia, serif;
        --font-sans: "Figtree", system-ui, sans-serif;
        --radius-sm: 0.375rem;
        --radius-md: 0.75rem;
        --radius-lg: 1.25rem;
        --measure: 65ch;
        --header-h: 4rem;
        color-scheme: light;
    }

    * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
    }

    html {
        scroll-behavior: auto;
    }

    body, html {
        font-family: ${props => props.theme.fonts.main};
        min-height: 100%;
        color: var(--fg);
        background-color: var(--bg);
        line-height: 1.6;
        -webkit-font-smoothing: antialiased;
    }

    h1, h2, h3, h4, h5 {
        font-family: var(--font-serif);
        font-weight: 600;
        line-height: 1.25;
        color: var(--fg);
    }

    img {
        max-width: 100%;
        height: auto;
        display: block;
    }

    a {
        color: var(--accent);
        text-underline-offset: 0.18em;
    }

    button {
        font-family: inherit;
        cursor: pointer;
    }

    :focus-visible {
        outline: 2px solid var(--accent);
        outline-offset: 3px;
    }
`

const components = {
    table: Table,
    pre: preProps => {
        const props = preToCodeBlock(preProps)
        // check if there is codeString and props
        if (props) {
            return <Code {...props} />
        }
        // return a pre without a code in it
        return <pre {...preProps} />
    },
    wrapper: ({ children }) => <>{children}</>,
}


export const wrapRootElement = ({ element }) => (
    <MDXProvider components={components}>
        <ThemeProvider theme={Theme}>
            <GlobalStyles />
            {element}
        </ThemeProvider>
    </MDXProvider>
)

function isFeedPath(pathname) {
  return pathname === "/" || /^\/\d+\/?$/.test(pathname)
}

let keepScroll = false
let lockedY = 0

export const onPreRouteUpdate = ({ location, prevLocation }) => {
  keepScroll = Boolean(
    prevLocation && isFeedPath(prevLocation.pathname) && isFeedPath(location.pathname)
  )
  if (keepScroll) lockedY = window.scrollY
}

export const shouldUpdateScroll = () => {
  if (keepScroll) return false
  return [0, 0]
}

export const onRouteUpdate = () => {
  if (!keepScroll) return
  const pin = () => window.scrollTo(0, lockedY)
  pin()
  window.requestAnimationFrame(pin)
  window.setTimeout(pin, 0)
  window.setTimeout(pin, 80)
}

